/**
 * Responsive column-fit calc for the dashboard table tile (S4 follow-up).
 * `tableTileColumns.tsx` gives every column a fixed default `size` (140px,
 * tuned — per its own comment — for a "4-of-12-col" tile) that never shrinks
 * to the ACTUAL tile width. A narrower tile (e.g. the common "3-of-12"
 * shape used by bound/wired table tiles) still overflows by a handful of
 * px, and because `TableTileView`'s table is always `table-layout: fixed`
 * with an explicit per-column pixel width, the overflow shows up as a plain
 * horizontal-scroll cutoff at the tile's own edge — a static/unscrolled view
 * shows the trailing column's header ("total" → "tota", "orders_cnt" →
 * "orders_cn…") hard-clipped by the tile boundary, never reaching the
 * column's OWN `truncate`/`title` ellipsis (that only engages once a column
 * is narrower than its own content, which never happens while the table is
 * simply wider than the tile and scrollable instead).
 *
 * Re-tuning the 140px constant again would only shift the same bug to the
 * next tile width that comes along (this already happened once — see
 * `tableTileColumns.tsx`'s "160 overflowed by ~20px" note). Instead, scale
 * DEFAULT (never manually resized) columns down to fit the tile's measured
 * width, floored at each column's own `minSize` — a column the user HAS
 * dragged is always left exactly as they set it, and scroll + the existing
 * right-edge fade remain the fallback once even `minSize` columns don't fit.
 */

export type FitColumnInput = {
  id: string;
  /** Current `column.getSize()` — the resized width if the user dragged this
   * column, otherwise the column def's default `size`. */
  size: number;
  minSize: number;
};

/**
 * @param containerWidth The table's scroll-container width in px, or `0`
 * when not yet measured (first paint, before the `ResizeObserver` fires) —
 * treated as "unknown", so every column keeps its natural size until the
 * real width is known.
 */
export function computeFitColumnWidths(
  columns: readonly FitColumnInput[],
  manuallyResizedIds: ReadonlySet<string>,
  containerWidth: number,
): Map<string, number> {
  const unresizedNatural = columns.reduce(
    (sum, c) => (manuallyResizedIds.has(c.id) ? sum : sum + c.size),
    0,
  );
  const manualTotal = columns.reduce(
    (sum, c) => (manuallyResizedIds.has(c.id) ? sum + c.size : sum),
    0,
  );
  const availableForUnresized =
    containerWidth > 0 ? Math.max(0, containerWidth - manualTotal) : unresizedNatural;
  const shrinkScale =
    unresizedNatural > 0 && availableForUnresized < unresizedNatural
      ? availableForUnresized / unresizedNatural
      : 1;

  return new Map(
    columns.map((c) => {
      if (shrinkScale >= 1 || manuallyResizedIds.has(c.id)) return [c.id, c.size] as const;
      return [c.id, Math.max(c.minSize, Math.floor(c.size * shrinkScale))] as const;
    }),
  );
}
